import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

export const dataViewScopeSchema = z.enum(["MARKETPLACE", "ORGANIZATION", "AGENCY"]);
export const dataViewVisibilitySchema = z.enum(["USER", "STAFF", "ANYONE"]);

export type DataViewScope = z.infer<typeof dataViewScopeSchema>;
export type DataViewVisibility = z.infer<typeof dataViewVisibilitySchema>;

const dataViewScopeDefinitionSchema = z.object({
  scope: dataViewScopeSchema,
  visibility: dataViewVisibilitySchema,
  entity_id: z.string(),
});

export type DataViewScopeDefinition = z.infer<typeof dataViewScopeDefinitionSchema>;

const dataViewSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  view_type: z.string(),
  dataset_key: z.string(),
  fields: z.array(z.unknown()),
  filters: z.array(z.unknown()),
  grouping: z.array(z.unknown()),
  sorting: z.array(z.unknown()),
  search: z.string(),
  scope_definition: dataViewScopeDefinitionSchema,
  marketplace: z.string().nullable(),
  created_by: z.number().nullable(),
  updated_by: z.number().nullable(),
  created_on: z.string(),
  updated_on: z.string(),
});

export type DataView = z.infer<typeof dataViewSchema>;

const dataViewListResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(dataViewSchema),
});

const dataViewWriteBodySchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  view_type: z.string(),
  dataset_key: z.string().optional(),
  fields: z.array(z.unknown()).optional(),
  filters: z.array(z.unknown()).optional(),
  grouping: z.array(z.unknown()).optional(),
  sorting: z.array(z.unknown()).optional(),
  search: z.string().optional(),
  scope_definition: dataViewScopeDefinitionSchema.optional(),
  marketplace: z.string().nullable().optional(),
});

export type DataViewWriteBody = z.infer<typeof dataViewWriteBodySchema>;

export const dataViewsApiDefinitions = prefixApi(
  "/api/data-views",
  apiBuilder({
    alias: "getDataViews",
    method: "get",
    path: "/",
    parameters: [
      { name: "view_type", type: "Query", schema: z.string().optional() },
      { name: "dataset_key", type: "Query", schema: z.string().optional() },
      { name: "marketplace", type: "Query", schema: z.string().optional() },
      { name: "scope", type: "Query", schema: dataViewScopeSchema.optional() },
      { name: "visibility", type: "Query", schema: dataViewVisibilitySchema.optional() },
    ],
    status: 200,
    response: dataViewListResponseSchema,
  })
    .addEndpoint({
      alias: "getDataView",
      method: "get",
      path: "/:id/",
      parameters: [{ name: "id", type: "Path", schema: z.number() }],
      status: 200,
      response: dataViewSchema,
    })
    .addEndpoint({
      alias: "createDataView",
      method: "post",
      path: "/",
      parameters: [{ name: "body", type: "Body", schema: dataViewWriteBodySchema }],
      status: 201,
      response: dataViewSchema,
    })
    .addEndpoint({
      alias: "updateDataView",
      method: "put",
      path: "/:id/",
      parameters: [
        { name: "id", type: "Path", schema: z.number() },
        { name: "body", type: "Body", schema: dataViewWriteBodySchema },
      ],
      status: 200,
      response: dataViewSchema,
    })
    .addEndpoint({
      alias: "deleteDataView",
      method: "delete",
      path: "/:id/",
      parameters: [{ name: "id", type: "Path", schema: z.number() }],
      status: 204,
      response: z.void(),
    })
    .build(),
);
